const HEADER_FIELDS = [
  { key: 'sales_date', label: 'Sales Date', type: 'text' },
  { key: 'patient_name', label: 'Patient Name', type: 'text' },
  { key: 'patient_id', label: 'Patient ID', type: 'text' },
  { key: 'patient_mobile', label: 'Patient Mobile', type: 'text' },
  { key: 'doctor_name', label: 'Doctor', type: 'text' },
  { key: 'customer_name', label: 'Customer', type: 'text' },
  { key: 'sales_adjustments', label: 'Adjustments', type: 'number' },
];

const LINE_FIELDS = [
  { key: 'line_description', label: 'Description', type: 'text' },
  { key: 'quantity', label: 'Quantity', type: 'number' },
  { key: 'rate', label: 'Rate', type: 'number' },
  { key: 'discount_percent', label: 'Discount %', type: 'number' },
  { key: 'line_total', label: 'Amount', type: 'number' },
];

const isBlank = (value) => value == null || String(value).trim() === '';

const toNumber = (value) => {
  if (isBlank(value)) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Canonical compare key: numbers ignore trailing zeros / string formatting. */
const compareKey = (value, type = 'text') => {
  if (type === 'number') {
    const n = toNumber(value);
    return n == null ? '' : n.toFixed(6);
  }
  return String(value ?? '').trim();
};

const displayValue = (value, type = 'text') => {
  if (isBlank(value)) return '—';
  if (type === 'number') {
    const n = toNumber(value);
    if (n == null) return String(value);
    // Prefer compact display: 1 instead of 1.000000, keep cents when needed
    return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(6)));
  }
  return String(value);
};

const deriveRate = (item = {}) => {
  const explicit = toNumber(item.rate);
  if (explicit != null) return explicit;
  const qty = toNumber(item.quantity);
  const total = toNumber(item.line_total);
  const discount = toNumber(item.discount_amount) || 0;
  // line_total is net of discount; add discount back to recover unit rate.
  if (qty && total != null) return (total + discount) / qty;
  return null;
};

const discountAmountToPercent = (item = {}) => {
  const rate = deriveRate(item);
  const qty = toNumber(item.quantity) || 0;
  const discount = toNumber(item.discount_amount) || 0;
  const gross = (rate || 0) * qty;
  if (!gross) return 0;
  return Number(((discount / gross) * 100).toFixed(4));
};

const lineFieldValue = (item, field) => {
  if (field.key === 'rate') return deriveRate(item);
  if (field.key === 'discount_percent') return discountAmountToPercent(item);
  return item?.[field.key];
};

const valuesEqual = (a, b, type = 'text') => compareKey(a, type) === compareKey(b, type);

const stableLineId = (item) => {
  if (item?.sales_record_id != null && item.sales_record_id !== '') {
    return `sr:${item.sales_record_id}`;
  }
  if (item?.item_id != null && item.item_id !== '') {
    return `item:${item.item_id}`;
  }
  return null;
};

const fieldChangesForLine = (prev, next) =>
  LINE_FIELDS.filter((field) => {
    const from = lineFieldValue(prev, field);
    const to = lineFieldValue(next, field);
    return !valuesEqual(from, to, field.type);
  }).map((field) => {
    const from = lineFieldValue(prev, field);
    const to = lineFieldValue(next, field);
    return {
      label: field.label,
      from: displayValue(from, field.type),
      to: displayValue(to, field.type),
    };
  });

/**
 * Build a readable list of changes between old_data and new_data snapshots.
 */
export function buildSalesEditDiff(oldData = {}, newData = {}) {
  const oldHeader = oldData.header || {};
  const newHeader = newData.header || {};
  const oldItems = Array.isArray(oldData.items) ? [...oldData.items] : [];
  const newItems = Array.isArray(newData.items) ? [...newData.items] : [];

  const headerChanges = HEADER_FIELDS.filter(
    (field) => !valuesEqual(oldHeader[field.key], newHeader[field.key], field.type)
  ).map((field) => ({
    label: field.label,
    from: displayValue(oldHeader[field.key], field.type),
    to: displayValue(newHeader[field.key], field.type),
  }));

  const usedOld = new Set();
  const lineChanges = [];

  newItems.forEach((next, newIndex) => {
    let oldIndex = -1;
    const nextId = stableLineId(next);

    if (nextId) {
      oldIndex = oldItems.findIndex(
        (item, idx) => !usedOld.has(idx) && stableLineId(item) === nextId
      );
    }
    if (oldIndex < 0 && !usedOld.has(newIndex) && oldItems[newIndex]) {
      oldIndex = newIndex;
    }
    if (oldIndex < 0 || usedOld.has(oldIndex) || !oldItems[oldIndex]) {
      lineChanges.push({
        type: 'added',
        label: next.line_description || `Line ${newIndex + 1}`,
        details: [
          `Qty ${displayValue(next.quantity, 'number')}`,
          `Rate ${displayValue(deriveRate(next), 'number')}`,
          `Discount ${displayValue(discountAmountToPercent(next), 'number')}%`,
          `Amount ${displayValue(next.line_total, 'number')}`,
        ],
      });
      return;
    }

    usedOld.add(oldIndex);
    const prev = oldItems[oldIndex];
    const fieldChanges = fieldChangesForLine(prev, next);
    if (fieldChanges.length) {
      lineChanges.push({
        type: 'changed',
        label: next.line_description || prev.line_description || `Line ${newIndex + 1}`,
        fieldChanges,
      });
    }
  });

  oldItems.forEach((item, index) => {
    if (usedOld.has(index)) return;
    lineChanges.push({
      type: 'removed',
      label: item.line_description || `Line ${index + 1}`,
      details: [
        `Qty ${displayValue(item.quantity, 'number')}`,
        `Rate ${displayValue(deriveRate(item), 'number')}`,
        `Discount ${displayValue(discountAmountToPercent(item), 'number')}%`,
        `Amount ${displayValue(item.line_total, 'number')}`,
      ],
    });
  });

  return {
    headerChanges,
    lineChanges,
    hasChanges: headerChanges.length > 0 || lineChanges.length > 0,
  };
}

export default buildSalesEditDiff;
